import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import { PropTypes as MobxPropType } from 'mobx-react';
import { apiCall } from '../../API';
import AsyncActionStore from '../AsyncActionStore';
import FlagStore from '../FlagStore';
import { PolicyModalSectionProps } from './PolicyModalStore';
import FuseSelectStore from '../FuseSelectStore';
import { ISOLATION_CHANNELS as ch } from '../ConsoleStore';

export const ISOLATION_CHANNELS_OPTIONS = [
    { value: ch.BROWSING_DEFENSE, label: 'Browser and Email Isolation' },
    { value: ch.SAAS_ISOLATION, label: 'SaaS Isolation' },
    { value: ch.URL_ISOLATION, label: 'URL Isolation' },
];

const DEFAULT_VALUES = {
    matchAllRoles: false,
    browsingRoles: [],
    channels: [],
    customerCategories: [],
    matchAllCategories: false,
    standardCategories: [],
};

export default class MatchCriteriaStore {
    static PropType = PropTypes.shape({
        matchAllRoles: FlagStore.PropType.isRequired,
        browsingRolesSelect: FuseSelectStore.PropType.isRequired,
        isolationChannelsSelect: FuseSelectStore.PropType.isRequired,
        allBrowsingRoles: MobxPropType.array,
        allGroupsHash: PropTypes.object,
        loadGroups: PropTypes.func,
        loadGroupsStatus: AsyncActionStore.PropType,
        loadRoles: PropTypes.func,
        loadRolesStatus: AsyncActionStore.PropType,
        referencesUnSyncedGroups: PropTypes.func,
        visibleSections: MobxPropType.array,
        hiddenSections: MobxPropType.array,
        setVisibleHiddenSections: PropTypes.func,

        ...PolicyModalSectionProps,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class MatchCriteriaStore */{
            matchAllRoles: new FlagStore(),
            browsingRolesSelect: new FuseSelectStore({
                sorterFactory: this.roleSorterFactory,
                filterFn: (browsingRole, filter) => {
                    const check = (entry) => entry.toLowerCase().indexOf(filter.toLowerCase()) > -1;
                    return check(browsingRole.name) ||
                        (browsingRole.effectsUserGroups || []).some(({ name }) => check(name)) ||
                        (browsingRole.effectsEmails || []).some(check);
                }
            }),
            isolationChannelsSelect: new FuseSelectStore({ options: ISOLATION_CHANNELS_OPTIONS }),
            matchAllCategories: new FlagStore(),
            apiListGroups: apiCall('/console/usergroups/list'),
            apiListRoles: apiCall('/console/browsing-roles/list'),
            loadRolesStatus: new AsyncActionStore(),
            loadGroupsStatus: new AsyncActionStore(),

            originalValue: {},
            allBrowsingRoles: [],
            allGroupsHash: {},
            filterChannelsFn: null,
            extraSections: {},
            visibleSections: [],
            hiddenSections: []
        }, data);
    }

    hashById = (arr) =>
        arr.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});

    loadGroups = () =>
        (this.loadGroupsStatus.isLoading ?
            this.loadGroupsStatus.promise :
            this.loadGroupsStatus.setLoading(
                this.apiListGroups()
                    .then((result) => this.hashById(result?.groups || []))
                    .then((groupsHash) => {
                        this.allGroupsHash = groupsHash;
                    })
            ).then(this.syncRoles, this.syncRoles));

    loadRoles = () => {
        if (!this.loadRolesStatus.isLoading) {
            this.loadRolesStatus.setLoading(
                this.apiListRoles()
                    .then((roles) => {
                        this.allBrowsingRoles = roles;
                    })
            );
        }

        return this.loadRolesStatus.promise.then(this.syncRoles, this.syncRoles);
    }

    // Load data in advance, so that when a modal is opened the groups are not marked as un-synced while groups load
    preload = () => {
        this.loadRoles();
        this.loadGroups();
        Object.values(this.extraSections).forEach((section) => section.preload && section.preload());
    }

    syncRoles = () => {
        if (!this.loadGroupsStatus.isLoading && !this.loadRolesStatus.isLoading) {
            this.browsingRolesSelect.setOptions(this.allBrowsingRoles.map(this.roleToOption));
        }
    }

    roleToOption = (item) => {
        const isDisabled = item.status?.toLowerCase() === 'disabled';
        const hasOutOfSyncGroups = item.effectsUserGroups?.some(({ id }) => !this.allGroupsHash[id]);

        let suffix;

        if (isDisabled) {
            suffix = ' (disabled)';
        } else if (hasOutOfSyncGroups) {
            suffix = ' (contains non synchronized group)';
        }
        return {
            value: this.roleToValue(item),
            label: item.name + (suffix || ''),
            allGroupsSynced: !hasOutOfSyncGroups,
            hidden: isDisabled,
            error: hasOutOfSyncGroups,
            ...item,
        };
    };

    roleToValue = (v) => v.id;

    sortHash = (r) =>
        `${r.status === 'DISABLED' ? 'A|' : 'Z|'}${!r.allGroupsSynced ? 'A|' : 'Z|'}${r.label}`;

    // The hash is only defined if this is the read only view
    roleSorterFactory = (options, hash = undefined) => {
        const optsHash = hash || this.hashById(options);
        return (v1, v2) => {
            const a = optsHash[v1];
            const b = optsHash[v2];
            return this.sortHash(a) > this.sortHash(b) ? 1 : -1;
        };
    }

    setValue = action((value) => {
        this.setVisibleHiddenSections([], []);
        const defaults = Object.assign({}, DEFAULT_VALUES);
        if (this.filterChannelsFn) {
            defaults.channels = ISOLATION_CHANNELS_OPTIONS.filter(
                (op) => !this.filterChannelsFn(op.value)
            ).map((v) => v.value);
        }
        value = { ...defaults, ...value };
        // In some edge cases the API has sometimes returned channels: null. That has been addressed,
        // adding this just to be extra safe
        value.channels = value.channels || [];
        value.standardCategories = value.standardCategories || [];
        // --
        this.originalValue = value;
        this.isDefault = value.isDefault;
        this.matchAllRoles.setValue(value.matchAllRoles);
        this.browsingRolesSelect.setOptions(value.browsingRoles.map(this.roleToOption));
        this.browsingRolesSelect.setSelectedValues(value.browsingRoles.map(this.roleToValue));

        this.isolationChannelsSelect.setSelectedValues([...value.channels]);

        Object.values(this.extraSections).forEach((section) => section.setValue(value));
    });

    selectBrowsingRole = action((item) => {
        this.browsingRolesSelect.setSelectedValues([
            ...this.browsingRolesSelect.selectedValues,
            this.roleToValue(item),
        ]);
    });

    setVisibleHiddenSections = action((visibleSections, hiddenSections) => {
        this.visibleSections.replace(visibleSections);
        this.hiddenSections.replace(hiddenSections);
    });

    getValue = () => ({
        matchAllRoles: this.matchAllRoles.value,
        browsingRoles: this.browsingRolesSelect.selectedValues,
        channels: this.isolationChannelsSelect.selectedValues,
        ...Object.values(this.extraSections).reduce((values, section) => ({ ...values, ...section.getValue() }), []),
    });

    isValid = () => {
        const v = this.getValue();
        const channels = (v.channels || []).filter((c) => !this.filterChannelsFn?.(c));

        return (v.matchAllRoles || v.browsingRoles.length > 0) && (this.isDefault || channels.length > 0) &&
            this.visibleSections.every((section) => !section.isValid || section.isValid());
    }

    isAllChannelsSelected = () => ISOLATION_CHANNELS_OPTIONS.every(({ value }) => {
        return this.isolationChannelsSelect.selectedValues.includes(value);
    });

    isUrlIsolationActive = () => {
        return this.isDefault || this.isolationChannelsSelect.selectedValues.includes(ch.URL_ISOLATION);
    }

    hasChanged = () =>
        this.originalValue.matchAllRoles !== this.matchAllRoles.value ||
        this.originalValue.browsingRoles.length !== this.browsingRolesSelect.selectedValues.length ||
        this.browsingRolesSelect.selectedValues
            .some((item, index) => item !== this.originalValue.browsingRoles[index].id) ||
        this.originalValue.channels.length !== this.isolationChannelsSelect.selectedValues.length ||
        this.isolationChannelsSelect.selectedValues
            .some((item, index) => item !== this.originalValue.channels[index]) ||
        Object.values(this.extraSections).some((section) => section.hasChanged());

    referencesUnSyncedGroups = () =>
        this.browsingRolesSelect.getSelectedItems().some((item) => !item.allGroupsSynced);

    referencesDisabledRoles = () =>
        this.browsingRolesSelect.getSelectedItems().some((item) => item.status === 'DISABLED');

    referencesInactiveChannels = () =>
        this.isolationChannelsSelect.getSelectedItems().some((item) => item.hidden);

    setFilterChannelsFunc = (filterFunc) => {
        this.filterChannelsFn = filterFunc;
    };

    setIsoChannelBasedOnLicense = () => {
        const options = ISOLATION_CHANNELS_OPTIONS.map((op) => {
            return Object.assign({}, op, { hidden: this.filterChannelsFn(op.value) });
        });
        this.isolationChannelsSelect.setOptions(options);
    }
}
